/**
 * IAM role hooks — list/create/detail/delete plus policy attach/detach. A role
 * carries a trust policy (`assume_role_policy_document`) supplied as JSON on
 * create and returned pretty-printed on detail. Scoped by `serviceId`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3, unwrap, s3Keys } from './_shared';
import { iamBase, iamPath, type AttachedPolicy } from './iam-shared';

/** One IAM role as listed (`GET /iam/roles`). */
export interface IamRole {
  role_name: string;
  role_id?: string;
  arn?: string;
  create_date?: string | null;
}

/** Full role with its trust document and attached policies (`GET /iam/roles/:name`). */
export interface IamRoleDetail {
  role: IamRole;
  /** Pretty-printed JSON trust policy (URL-decoded server-side). */
  assume_role_policy_document: string;
  attached_policies: AttachedPolicy[];
}

export interface CreateRoleInput {
  role_name: string;
  /** JSON trust policy document as a string (validated client-side before send). */
  assume_role_policy_document: string;
}

/** List the service's IAM roles. */
export function useIamRoles(serviceId: string | undefined) {
  return useQuery<IamRole[]>({
    queryKey: s3Keys.iamRoles(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${iamBase(serviceId!)}/roles`);
      return unwrap<{ roles?: IamRole[] }>(res)?.roles ?? [];
    },
  });
}

/** Fetch one role with its trust document and attached policies. */
export function useIamRole(serviceId: string | undefined, name: string | undefined) {
  return useQuery<IamRoleDetail>({
    queryKey: s3Keys.iamRole(serviceId ?? '', name ?? ''),
    enabled: !!serviceId && !!name,
    queryFn: async () =>
      unwrap<IamRoleDetail>(await serviceApiV3.get(iamPath(serviceId!, 'roles', name!))),
  });
}

/** Create an IAM role from a name + JSON trust policy. */
export function useCreateIamRole(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<IamRole, Error, CreateRoleInput>({
    mutationFn: async (input) =>
      unwrap<IamRole>(await serviceApiV3.post(`${iamBase(serviceId)}/roles`, input)),
    onSuccess: () => qc.invalidateQueries({ queryKey: s3Keys.iamRoles(serviceId) }),
  });
}

/** Delete a role by name. */
export function useDeleteIamRole(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (name) => {
      await serviceApiV3.delete(iamPath(serviceId, 'roles', name));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: s3Keys.iamRoles(serviceId) }),
  });
}

/** Attach or detach a managed policy on a role (`attach` toggles verb). */
export function useRolePolicyMutation(serviceId: string, attach: boolean) {
  const qc = useQueryClient();
  return useMutation<void, Error, { name: string; policyArn: string }>({
    mutationFn: async ({ name, policyArn }) => {
      const verb = attach ? 'attach-policy' : 'detach-policy';
      await serviceApiV3.post(iamPath(serviceId, 'roles', name, `/${verb}`), {
        policy_arn: policyArn,
      });
    },
    onSuccess: (_r, { name }) =>
      qc.invalidateQueries({ queryKey: s3Keys.iamRole(serviceId, name) }),
  });
}
